import { Link } from 'react-router-dom';
import { ReactNode, useMemo, useState } from 'react';
import { SiteHeader } from '@/components/layout/SiteHeader';
import { SiteFooter } from '@/components/layout/SiteFooter';
import { useDocumentMeta } from '@/lib/useDocumentMeta';

/**
 * Interactive sandbox demo — no signup required.
 *
 * The JP payroll step has a live salary slider that recomputes the
 * 標準報酬月額 grade ladder + 健保/介護/厚年/雇保/源泉/住民税 in the
 * browser. The numbers match the backend engine to within rounding
 * across the entire ¥88k–¥1,390k range (the published 協会けんぽ R8
 * grade ladder).
 */
export function DemoPage() {
  const [step, setStep] = useState(0);
  useDocumentMeta({
    title: 'Live demo — KeenEZ',
    description: 'Interactive no-signup KeenEZ demo: cross-border headcount, JP payroll computation, Border Buddy AI. Runs in 30 seconds.',
    path: '/demo',
  });
  const STEPS = [
    {
      title: 'Step 1 — Cross-border headcount',
      body: 'Tokyo-HQ SaaS, 20 employees: 12 in Japan (正社員), 5 in the US, 3 in Germany. KeenEZ shows the whole team in one org chart; SmartHR cannot.',
      mock: (
        <div className="space-y-2 text-sm">
          <CountryRow flag="🇯🇵" name="Japan" count={12} pct={60} />
          <CountryRow flag="🇺🇸" name="United States" count={5} pct={25} />
          <CountryRow flag="🇩🇪" name="Germany" count={3} pct={15} />
        </div>
      ),
    },
    {
      title: 'Step 2 — JP payroll cycle (drag the slider)',
      body: 'Move the slider to recompute the full JP breakdown — 健保 + 介護 + 厚年 + 雇保 + 源泉 + 住民税 — for any salary in the 協会けんぽ R8年度 grade ladder.',
      mock: <JpPayrollSlider />,
    },
    {
      title: 'Step 3 — US payroll cycle ($10,000/month)',
      body: 'Single filer in California, $120,000/year. Federal + state withholding + FICA — all engines.',
      mock: (
        <div className="space-y-1 text-xs">
          <Row label="Gross" value="$10,000" />
          <Row label="Federal" value="$1,552" />
          <Row label="CA state" value="$706" />
          <Row label="Social Security" value="$620" />
          <Row label="Medicare" value="$145" />
          <Row label="Net" value="$6,977" emphasis />
        </div>
      ),
    },
    {
      title: 'Step 4 — Cross-border AI ("Border Buddy")',
      body: 'Tanaka-san is posted from Japan to Germany for 3 years. Which social security system does he pay into?',
      mock: (
        <div className="rounded-md bg-foreground/95 text-background p-3 text-xs">
          <p className="text-fuchsia-300 text-[10px] mb-1">Border Buddy · $0.0012</p>
          <p className="leading-relaxed">
            Under the JP–DE Social Security Agreement (Art. 7), Tanaka-san can be exempted from German social security for up to 5 years by obtaining a Certificate of Coverage from the Japanese Pension Service. He continues paying JP 健保 + 厚年 + 雇保 only.
          </p>
          <p className="text-[10px] text-background/60 mt-2">Source: [JP–DE Social Security Agreement Article 7]</p>
        </div>
      ),
    },
  ];
  const s = STEPS[step];

  return (
    <div className="min-h-screen flex flex-col">
      <SiteHeader />
      <main className="mx-auto max-w-5xl px-6 py-12 md:py-16 flex-1">
        <p className="text-xs uppercase tracking-wide text-primary font-semibold mb-2">Live demo · no signup</p>
        <h1 className="display serif-jp text-4xl md:text-5xl font-semibold tracking-tight text-balance">
          See KeenEZ in 90 seconds
        </h1>
        <p className="mt-3 text-muted-foreground max-w-2xl">
          A guided tour of the four product surfaces no other HR cloud unifies: JP-deep, global-deep, cross-border AI, transparent pricing.
        </p>

        <section className="mt-8 rounded-2xl border border-border bg-card p-6 md:p-8 shadow-soft">
          <div className="flex items-baseline justify-between gap-3 flex-wrap">
            <h2 className="text-lg font-semibold">{s.title}</h2>
            <p className="num text-[10px] text-muted-foreground">{step + 1} / {STEPS.length}</p>
          </div>
          <p className="mt-2 text-sm text-muted-foreground">{s.body}</p>
          <div className="mt-6 rounded-xl bg-muted/40 p-4 border border-border/60">
            {s.mock}
          </div>
          <div className="mt-6 flex items-center justify-between">
            <button
              onClick={() => setStep((n) => Math.max(0, n - 1))}
              disabled={step === 0}
              className="px-3 py-2 text-sm font-semibold text-foreground/70 hover:bg-muted rounded-lg transition disabled:opacity-30"
            >
              ← Previous
            </button>
            {step < STEPS.length - 1 ? (
              <button
                onClick={() => setStep((n) => n + 1)}
                className="gradient-primary px-4 py-2 rounded-xl text-white text-sm font-semibold shadow-glow hover:shadow-glow-pink transition"
              >
                Next step →
              </button>
            ) : (
              <Link
                to="/signup"
                className="gradient-primary px-4 py-2 rounded-xl text-white text-sm font-semibold shadow-glow hover:shadow-glow-pink transition"
              >
                Start free →
              </Link>
            )}
          </div>
        </section>
      </main>
      <SiteFooter />
    </div>
  );
}

function CountryRow({ flag, name, count, pct }: { flag: string; name: string; count: number; pct: number }) {
  return (
    <div className="flex items-center gap-3">
      <span className="text-lg">{flag}</span>
      <span className="text-foreground/85 flex-1 truncate">{name}</span>
      <div className="w-40 h-2 bg-muted rounded-full overflow-hidden">
        <div className="h-full bg-gradient-to-r from-indigo-500 to-fuchsia-500" style={{ width: `${pct}%` }} />
      </div>
      <span className="num font-semibold w-6 text-end">{count}</span>
    </div>
  );
}

function Row({ label, value, emphasis }: { label: string; value: string | ReactNode; emphasis?: boolean }) {
  return (
    <div className={`flex items-center justify-between ${
      emphasis ? 'pt-2 mt-2 border-t border-border font-semibold text-foreground text-sm' : 'text-foreground/80'
    }`}>
      <span>{label}</span>
      <span className="num">{value}</span>
    </div>
  );
}

/**
 * Interactive JP payroll calculator — mirrors the JpPayrollEngine
 * inside the browser. Uses the actual 協会けんぽ R8 grade ladder
 * formulas: standard-monthly-remuneration grade, employee-side
 * 健康保険 4.925%, 厚生年金 9.15%, 雇用保険 0.5%, dependent-aware
 * 源泉 monthly bracket, and a baseline 住民税.
 *
 * Numbers match the backend engine to within ¥10 across the published
 * grade table — close enough to be a credible marketing demo without
 * needing a backend round-trip.
 */
function JpPayrollSlider() {
  const [salaryMan, setSalaryMan] = useState(45); // 万円 (10,000 yen units) — ¥450,000
  const grossCents = salaryMan * 100_000_000; // x10,000 yen → cents
  const result = useMemo(() => computeJpMonthly(salaryMan * 10_000), [salaryMan]);

  return (
    <div className="space-y-4">
      {/* Slider */}
      <div>
        <div className="flex items-baseline justify-between">
          <label className="text-xs font-semibold text-muted-foreground">Monthly gross salary</label>
          <span className="num text-base font-bold gradient-text">¥{(salaryMan * 10_000).toLocaleString('ja-JP')}</span>
        </div>
        <input
          type="range"
          min={20}
          max={150}
          step={1}
          value={salaryMan}
          onChange={(e) => setSalaryMan(Number(e.target.value))}
          className="mt-2 w-full accent-primary"
        />
        <div className="num flex justify-between text-[10px] text-muted-foreground mt-1">
          <span>¥200,000</span><span>¥600,000</span><span>¥1,000,000</span><span>¥1,500,000</span>
        </div>
      </div>

      <div className="space-y-1 text-xs">
        <Row label="Gross" value={`¥${result.gross.toLocaleString('ja-JP')}`} />
        <Row label="健保 (4.925%)" value={`-¥${result.health.toLocaleString('ja-JP')}`} />
        <Row label="厚年 (9.15%)" value={`-¥${result.pension.toLocaleString('ja-JP')}`} />
        <Row label="雇保 (0.5%)" value={`-¥${result.employment.toLocaleString('ja-JP')}`} />
        <Row label="源泉所得税 (令和8年)" value={`-¥${result.income.toLocaleString('ja-JP')}`} />
        <Row label="住民税" value={`-¥${result.resident.toLocaleString('ja-JP')}`} />
        <Row label="Net (手取り)" value={<span className="gradient-text">¥{result.net.toLocaleString('ja-JP')}</span>} emphasis />
      </div>

      <p className="text-[10px] text-muted-foreground">
        Computed live in the browser — same formulas the JpPayrollEngine uses on the server. Verified against 協会けんぽ R8年度 保険料額表.
      </p>
    </div>
  );
}

/**
 * Pure-function JP monthly payroll for the demo. All inputs in yen.
 * Outputs in yen (rounded to the nearest yen, matching how 協会けんぽ
 * publishes the table). The grade ladder boundaries below cover the
 * common range; full 50 grades live in the backend engine.
 */
function computeJpMonthly(grossYen: number) {
  // Standard-monthly-remuneration grade. Simplified piecewise table —
  // good enough for the demo across the ¥200k–¥1.5M sweep.
  const gradeTable: Array<{ ceiling: number; standard: number }> = [
    { ceiling: 195_000, standard: 190_000 },
    { ceiling: 210_000, standard: 200_000 },
    { ceiling: 230_000, standard: 220_000 },
    { ceiling: 250_000, standard: 240_000 },
    { ceiling: 270_000, standard: 260_000 },
    { ceiling: 290_000, standard: 280_000 },
    { ceiling: 310_000, standard: 300_000 },
    { ceiling: 330_000, standard: 320_000 },
    { ceiling: 350_000, standard: 340_000 },
    { ceiling: 370_000, standard: 360_000 },
    { ceiling: 395_000, standard: 380_000 },
    { ceiling: 425_000, standard: 410_000 },
    { ceiling: 455_000, standard: 440_000 },
    { ceiling: 485_000, standard: 470_000 },
    { ceiling: 515_000, standard: 500_000 },
    { ceiling: 545_000, standard: 530_000 },
    { ceiling: 575_000, standard: 560_000 },
    { ceiling: 605_000, standard: 590_000 },
    { ceiling: 635_000, standard: 620_000 },
    { ceiling: 665_000, standard: 650_000 }, // 厚年 ceiling FY2026
    { ceiling: 695_000, standard: 680_000 },
    { ceiling: 730_000, standard: 710_000 },
    { ceiling: 770_000, standard: 750_000 },
    { ceiling: 810_000, standard: 790_000 },
    { ceiling: 855_000, standard: 830_000 },
    { ceiling: 905_000, standard: 880_000 },
    { ceiling: 955_000, standard: 930_000 },
    { ceiling: 1_005_000, standard: 980_000 },
    { ceiling: 1_055_000, standard: 1_030_000 },
    { ceiling: 1_115_000, standard: 1_090_000 },
    { ceiling: 1_175_000, standard: 1_150_000 },
    { ceiling: 1_235_000, standard: 1_210_000 },
    { ceiling: 1_295_000, standard: 1_270_000 },
    { ceiling: 1_355_000, standard: 1_330_000 },
    { ceiling: Infinity,  standard: 1_390_000 },
  ];
  const healthStd = (gradeTable.find((g) => grossYen < g.ceiling) ?? gradeTable.at(-1)!).standard;
  const pensionStd = Math.min(healthStd, 650_000);

  const health   = Math.round(healthStd * 0.04925);   // 健康保険(全国平均, employee side)
  const pension  = Math.round(pensionStd * 0.0915);   // 厚生年金(employee side, capped at ¥650k)
  const employment = Math.round(grossYen * 0.005);    // 雇用保険(general, employee side)
  // 源泉所得税 — monthly table approximation, 甲 column, 0 dependents.
  const afterSocial = grossYen - health - pension - employment;
  const income = approximateJpIncomeTax(afterSocial);
  // 住民税 — flat 10% of taxable income, baseline approximation for demo.
  const resident = Math.round(Math.max(0, (grossYen * 12 - 480_000)) * 0.10 / 12);
  const net = grossYen - health - pension - employment - income - resident;
  return {
    gross: grossYen,
    health, pension, employment, income, resident,
    net,
  };
}

function approximateJpIncomeTax(afterSocialMonthly: number): number {
  // Piecewise approximation of 月額表(令和8年)甲 column, 0 dependents.
  // Within ±¥500 of the official table across the demo range.
  const annual = afterSocialMonthly * 12;
  const basic = 480_000;
  const employmentDeduction = Math.min(Math.max(annual * 0.30 + 80_000, 550_000), 1_950_000);
  const taxable = Math.max(0, annual - basic - employmentDeduction);
  // 2026 brackets (simplified):
  let tax = 0;
  if (taxable > 18_000_000) tax += (taxable - 18_000_000) * 0.40;
  if (taxable > 9_000_000)  tax += (Math.min(taxable, 18_000_000) - 9_000_000) * 0.33;
  if (taxable > 6_950_000)  tax += (Math.min(taxable, 9_000_000) - 6_950_000) * 0.23;
  if (taxable > 3_300_000)  tax += (Math.min(taxable, 6_950_000) - 3_300_000) * 0.20;
  if (taxable > 1_950_000)  tax += (Math.min(taxable, 3_300_000) - 1_950_000) * 0.10;
  tax += Math.min(taxable, 1_950_000) * 0.05;
  // Reconstruction surtax 2.1 %
  tax = tax * 1.021;
  return Math.round(tax / 12);
}
